/* eslint complexity: ["error", {max: 11}] -- applies to entire file */
/* eslint max-lines-per-function: ["error", {max: 180}] -- applies to entire file */
/* eslint max-statements: ["error", {max: 25}] -- applies to entire file */

import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import type { ModalPath, ScreenPath, TabPath } from "@/constants/Navigation";
import { SMTEntryType } from "@/constants/SMT";
import { FABTestID } from "@/constants/TestID";
import useAddSMTEntry from "@/hooks/useAddSMTEntry";
import useAppUserSMTPermissions from "@/hooks/useAppUserSMTPermissions";
import { useModalTracker } from "@/hooks/useModalTracker";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import type { AddMealRequestBody } from "@/store/api/meals";
import { useGetSMTConfigQuery } from "@/store/api/smt";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectAddSMTEntrySelectedPatientId,
  selectSMTPhoto,
  useSMTDispatch,
} from "@/store/reducers/smt";
import AddSMTEntryBottomSheetModal from "@/ui/components/smt/AddSMTEntry/AddSMTEntryBottomSheetModal";
import AddSMTEntryImagePickerModal from "@/ui/components/smt/AddSMTEntry/AddSMTEntryImagePickerModal";
import AddSMTEntryPatientSelectorModal from "@/ui/components/smt/AddSMTEntry/AddSMTEntryPatientSelectorModal";
import type { ToastRequest } from "@/ui/composed/toast/Toast";
import { FAIconName, FloatingActionButton } from "@/ui/core";

enum AddSMTEntryToastMessage {
  AddMealFailure = "Failed to add meal, please try again",
  AddMealSuccess = "Meal added successfully",
  UploadMealPhotoFailure = "Failed to upload meal photo",
}

type SMTScreenPath = ScreenPath | TabPath | ValueOf<typeof ModalPath>;

interface AddSMTEntryFlowProps {
  entryType?: SMTEntryType;
  onAnimationComplete?: () => void;
  screenPath: SMTScreenPath;
  shouldAnimateFAB?: boolean;
  shouldOpenImmediately?: boolean;
}

const AddSMTEntryFlow = ({
  // we will be expanding this to support additional entry flows later (not ticketed yet)
  entryType = SMTEntryType.Meal,
  onAnimationComplete,
  screenPath,
  shouldAnimateFAB = false,
  shouldOpenImmediately = false,
}: AddSMTEntryFlowProps) => {
  const { activeModal } = useModalTracker();

  const { data: smtConfig } = useGetSMTConfigQuery();
  const addFoodUIConfig = smtConfig?.uiConfig.addFood;

  const selectedPatientId = useAppSelector(selectAddSMTEntrySelectedPatientId);
  const photo = useAppSelector(selectSMTPhoto);

  const [
    isAddSMTEntryBottomSheetModalOpen,
    setIsAddSMTEntryBottomSheetModalOpen,
  ] = useState<boolean>(false);

  const { track } = useAnalytics();

  const {
    resetSMTState,
    setAddSMTEntrySelectedPatientId,
    setIsFilePickerModalOpen,
    setIsSelectPatientModalOpen,
  } = useSMTDispatch();

  const { externalId: appUserId, isPatient: isAppUserPatient } =
    useAppUserSelector();

  const {
    supportPatientsEligibleForSMT: { supportPatientsWithSMTAddAccess },
  } = useAppUserSMTPermissions();

  const primaryPatientId = isAppUserPatient ? appUserId : selectedPatientId;

  const { saveMeal } = useAddSMTEntry();
  const { showToast } = useToast();

  const handleFABPress = () => {
    track(SMTEntryAnalyticsEvent.AddSMTEntryPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.FloatingButton,
      [AnalyticsFieldName.Source]: screenPath,
    });

    // patients go ahead to add their SMT entry
    if (isAppUserPatient) {
      setIsFilePickerModalOpen(true);
      return;
    }

    // supports/proxies: only show patient selector if they have 2+ patients with SMT add access
    if (supportPatientsWithSMTAddAccess.length >= 2) {
      setIsSelectPatientModalOpen(true);
      return;
    }

    // If they have exactly 1 patient, skip the selector and proceed directly
    if (supportPatientsWithSMTAddAccess.length === 1) {
      setAddSMTEntrySelectedPatientId(
        supportPatientsWithSMTAddAccess[0].externalId,
      );
      setIsFilePickerModalOpen(true);
    }
  };

  const handleAssetPickerLaunch = () => {
    // On iOS - Ensure that the bottom sheet is closed before opening the asset picker, otherwise the asset picker will be rendered behind the bottom sheet and cause a poor user experience
    setIsAddSMTEntryBottomSheetModalOpen(false);
  };

  const handleAssetSelected = () => {
    setIsAddSMTEntryBottomSheetModalOpen(true);
  };

  const handleUploadComplete = (isSuccess: boolean) => {
    const message = isSuccess
      ? AddSMTEntryToastMessage.AddMealSuccess
      : AddSMTEntryToastMessage.UploadMealPhotoFailure;

    const variant: ToastRequest["variant"] = isSuccess ? "success" : "error";

    showToast({
      text: message,
      variant,
    });
  };

  const handleSaveSuccess = () => {
    // Show success toast immediately if no photo
    if (!photo) {
      showToast({
        text: AddSMTEntryToastMessage.AddMealSuccess,
        variant: "success",
      });
    }
    setIsAddSMTEntryBottomSheetModalOpen(false);
  };

  const handleSaveFailure = () => {
    showToast({
      text: AddSMTEntryToastMessage.AddMealFailure,
      variant: "error",
    });
  };

  const handleSaveMeal = async (params: AddMealRequestBody) => {
    const smtEntryId = await saveMeal({
      onUploadComplete: handleUploadComplete,
      params,
    });

    if (smtEntryId) {
      handleSaveSuccess();
    } else {
      handleSaveFailure();
    }
  };

  const handleDismiss = () => {
    track(SMTEntryAnalyticsEvent.DismissEntryModalPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.FloatingButton,
      [AnalyticsFieldName.Source]: screenPath,
    });
    setIsAddSMTEntryBottomSheetModalOpen(false);
    resetSMTState();
  };

  useEffect(() => {
    if (!shouldOpenImmediately) return;
    handleFABPress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpenImmediately]);

  // if there is a modal currently rendered, and this flow is not part of that modal, un-render this flow
  // to prevent issues of double rendering (or performance impact) or potentially weird unexpected edge cases
  if (!isNil(activeModal) && activeModal !== screenPath) {
    return null;
  }

  return (
    <>
      <FloatingActionButton
        accessibilityLabel="Add a meal entry"
        iconName={FAIconName.Plus}
        onAnimationComplete={onAnimationComplete}
        onPress={handleFABPress}
        shouldAnimateBounce={shouldAnimateFAB}
        testID={FABTestID.SelfMonitoring}
      />
      {primaryPatientId && (
        <AddSMTEntryBottomSheetModal
          entryType={entryType}
          isOpen={isAddSMTEntryBottomSheetModalOpen}
          onDismiss={handleDismiss}
          onSaveEntry={handleSaveMeal}
          patientExternalId={primaryPatientId}
          screenPath={screenPath}
          uiConfig={addFoodUIConfig?.entryForm}
        />
      )}
      <AddSMTEntryPatientSelectorModal />
      <AddSMTEntryImagePickerModal
        onAssetPickerLaunch={handleAssetPickerLaunch}
        onAssetSelected={handleAssetSelected}
        uiConfig={addFoodUIConfig?.addPhoto}
      />
    </>
  );
};

export { AddSMTEntryToastMessage };
export default AddSMTEntryFlow;
export type { SMTScreenPath };
